import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Check, ChevronDown, X, Sparkles, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/lib/auth";

const STORAGE_KEY_BASE = "calm-trading.onboarding.v1";

// Milestones je Nutzergruppe. Struktur und Sprache sind bewusst unterschiedlich,
// damit fortgeschrittene Nutzer:innen keine Einsteiger-Erklärungen bekommen —
// und Anfänger:innen nicht mit Experten-Sprache erschlagen werden.
const MILESTONES_BY_STAGE = {
  orientierung: [
    {
      key: "start",
      day: "Schritt 1",
      title: "Ankommen",
      lead: "Konto einrichten, Broker verbinden (optional) und den ersten Trade erfassen. Kein Druck, wir gehen langsam.",
      tasks: [
        { id: "profile", label: "Anzeigename und Basiswährung in den Einstellungen prüfen", to: "/app/einstellungen" },
        { id: "quiz", label: "Wissens-Quiz starten — nach 5 Minuten kennt der Coach dein Niveau", to: "/app/quiz" },
        { id: "first-trade", label: "Deinen ersten Trade als Notiz erfassen (echt oder Paper-Trade)", to: "/app/journal" },
      ],
    },
    {
      key: "erklaeren",
      day: "Schritt 2",
      title: "Ersten Trade erklären",
      lead: "Warum hast du gekauft? Was hättest du beobachten müssen? Der Coach hilft beim Formulieren.",
      tasks: [
        { id: "journal-note", label: "Zu einem Trade eine kurze Journal-Notiz ergänzen (was, warum, was gelernt)", to: "/app/journal" },
        { id: "coach-first", label: "Dem KI-Coach eine Anfänger-Frage stellen — nichts ist zu simpel", to: "/app/ki-coach" },
      ],
    },
    {
      key: "erste-woche",
      day: "Schritt 3",
      title: "Erste Woche zurückblicken",
      lead: "Welche Zahlen fallen auf? Was war Zufall, was Muster? Klein anfangen.",
      tasks: [
        { id: "review-analytics", label: "Analyse einmal aufmachen — welche Zahl überrascht dich?", to: "/app/analyse" },
        { id: "coach-week", label: "Coach fragen: Was habe ich diese Woche gelernt?", to: "/app/ki-coach" },
      ],
    },
  ],
  struktur: [
    {
      key: "fundament",
      day: "Schritt 1",
      title: "Setup dokumentieren",
      lead: "Halte in einer Strategie fest: Setup, Entry-Trigger, Stop-Regel, Ziel-Logik, Größe pro Trade.",
      tasks: [
        { id: "strategy", label: "Eine Strategie anlegen oder überarbeiten (Kernidee in 5 Sätzen)", to: "/app/strategien" },
        { id: "quiz", label: "Wissens-Quiz durchgehen — Lücken sichtbar machen, Coach kalibrieren", to: "/app/quiz" },
        { id: "broker", label: "Broker-API verbinden, damit alle Trades sauber automatisch landen", to: "/app/einstellungen" },
      ],
    },
    {
      key: "prozess",
      day: "Schritt 2",
      title: "Reflexions-Loop einführen",
      lead: "Nach jedem Trade: Regelkonform ja/nein und ein kurzes Learning. Das ist der eigentliche Edge-Builder.",
      tasks: [
        { id: "journal-note", label: "Bei den letzten 3 Trades die Regelkonformität explizit markieren", to: "/app/journal" },
        { id: "coach-first", label: "Coach nach Mustern in deinen bisherigen Reflexionen fragen", to: "/app/ki-coach" },
      ],
    },
    {
      key: "kennzahlen",
      day: "Schritt 3",
      title: "Erste harte Zahlen ziehen",
      lead: "Trefferquote, durchschnittliches CRV, größte Verlust-Serie — was sagt dein System wirklich?",
      tasks: [
        { id: "review-analytics", label: "Analyse: Trefferquote und CRV der letzten 20 Trades notieren", to: "/app/analyse" },
        { id: "focus", label: "Einen konkreten Fokus für die nächsten zwei Wochen formulieren", to: "/app" },
      ],
    },
  ],
  optimierung: [
    {
      key: "bestand",
      day: "Schritt 1",
      title: "Wo bist du gerade?",
      lead: "Bring deine Zahlen in TradeMind rein und benenne die größte offene Baustelle klar beim Namen.",
      tasks: [
        { id: "broker", label: "Alle aktiven Konten per API anbinden — Vollbild deiner Ausführung", to: "/app/einstellungen" },
        { id: "strategy", label: "Aktuelle Strategien-Regeln prüfen: was ist noch dokumentiert, was schon Reflex?", to: "/app/strategien" },
        { id: "quiz", label: "Wissens-Quiz auf Expertenmodus — Blindspots sauber sichtbar machen", to: "/app/quiz" },
      ],
    },
    {
      key: "analyse",
      day: "Schritt 2",
      title: "Muster präzise trennen",
      lead: "Nicht «gute» und «schlechte» Trades — sondern regelkonforme und nicht-regelkonforme, mit und ohne Setup-Match.",
      tasks: [
        { id: "review-analytics", label: "R-Verteilung, Regelkonformität und Fehler-Tags im Detail durchsehen", to: "/app/analyse" },
        { id: "coach-first", label: "Coach: Wo liegt bei mir statistisch der größte Erwartungswert-Leak?", to: "/app/ki-coach" },
      ],
    },
    {
      key: "eingriff",
      day: "Schritt 3",
      title: "Eingriff planen",
      lead: "Eine gezielte Änderung, klar formuliert, mit Erfolgsmetrik und Überprüfungshorizont.",
      tasks: [
        { id: "focus", label: "Fokus-Karte auf dem Dashboard mit einer einzigen Verbesserung setzen", to: "/app" },
        { id: "coach-week", label: "Wochenreview mit dem Coach: Hypothese, Nachweis, nächster Schritt", to: "/app/ki-coach" },
      ],
    },
  ],
};

const STAGE_INTRO = {
  orientierung: {
    eyebrow: "Deine ersten Schritte",
    heading: "Willkommen bei TradeMind",
    lead: "Wir gehen zusammen: Konto einrichten, ersten Trade erfassen, im Coach nachfragen. Ohne Druck.",
    done: "Solide erste Etappe. Der Coach nimmt dich ab jetzt automatisch mit — auch wenn dieses Onboarding weg ist.",
  },
  struktur: {
    eyebrow: "Struktur schaffen",
    heading: "Bring Ordnung in deinen Handel",
    lead: "Du hast schon Erfahrung. Jetzt geht es darum, das Regelwerk sauber zu dokumentieren, den Prozess konsistent zu machen und die ersten harten Zahlen zu ziehen.",
    done: "Prozess steht. Wechsel bei Bedarf oben rechts in »Optimierung« — der Coach passt sich an.",
  },
  optimierung: {
    eyebrow: "Feintuning",
    heading: "Optimierung — an der richtigen Schraube drehen",
    lead: "Kein Onboarding im klassischen Sinn. Nutze diese Karte, um deinen Verbesserungsprozess kurz zu strukturieren: Bestandsaufnahme, präzise Analyse, gezielter Eingriff.",
    done: "Diesen Zyklus regelmäßig wiederholen — z. B. quartalsweise oder nach 100 Trades.",
  },
};

const loadState = (stage) => {
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY_BASE}:${stage}`);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return { done: {}, dismissed: false, expanded: null };
};

const saveState = (stage, s) => {
  try {
    localStorage.setItem(`${STORAGE_KEY_BASE}:${stage}`, JSON.stringify(s));
  } catch { /* ignore */ }
};

export default function OnboardingCard() {
  const { user } = useAuth() || {};
  const stage = user?.onboarding?.stage || "orientierung";
  const MILESTONES = MILESTONES_BY_STAGE[stage] || MILESTONES_BY_STAGE.orientierung;
  const intro = STAGE_INTRO[stage] || STAGE_INTRO.orientierung;
  const [state, setState] = useState(() => loadState(stage));

  // Bei Stage-Wechsel (z. B. Admin-Simulation) State frisch laden, damit der
  // Fortschritt der jeweiligen Gruppe unabhängig gespeichert ist.
  useEffect(() => { setState(loadState(stage)); }, [stage]);
  useEffect(() => { saveState(stage, state); }, [stage, state]);

  const allTasks = useMemo(() => MILESTONES.flatMap((m) => m.tasks.map((t) => t.id)), [MILESTONES]);
  const doneCount = allTasks.filter((id) => state.done[id]).length;
  const total = allTasks.length;
  const pct = total ? (doneCount / total) * 100 : 0;
  const complete = doneCount === total;

  if (state.dismissed) return null;

  const toggleTask = (id) =>
    setState((s) => ({ ...s, done: { ...s.done, [id]: !s.done[id] } }));
  const toggleExpand = (key) =>
    setState((s) => ({ ...s, expanded: s.expanded === key ? null : key }));
  const dismiss = () => setState((s) => ({ ...s, dismissed: true }));

  return (
    <section
      className="card-surface p-6 md:p-8 mb-8 relative overflow-hidden"
      data-testid="onboarding-card"
    >
      <button
        onClick={dismiss}
        className="absolute top-4 right-4 h-8 w-8 grid place-items-center rounded-full text-muted-foreground hover:bg-secondary transition-colors"
        aria-label="Onboarding ausblenden"
        data-testid="onboarding-dismiss"
      >
        <X className="h-4 w-4" strokeWidth={1.5} />
      </button>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div className="max-w-2xl">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" strokeWidth={1.5} />
            <div className="overline">{intro.eyebrow}</div>
          </div>
          <h2 className="font-heading text-2xl md:text-3xl mt-2 tracking-tight">
            {complete ? intro.done.split(".")[0] + "." : intro.heading}
          </h2>
          <p className="mt-2 text-sm md:text-base text-muted-foreground leading-relaxed">
            {complete ? intro.done : intro.lead}
          </p>
        </div>
        <div className="flex items-center gap-4 min-w-[220px]">
          <div className="flex-1">
            <div className="flex items-baseline justify-between text-xs text-muted-foreground mb-2">
              <span>Fortschritt</span>
              <span className="font-mono">
                {doneCount} / {total}
              </span>
            </div>
            <Progress value={pct} className="h-1.5" data-testid="onboarding-progress" />
          </div>
        </div>
      </div>

      <div className="mt-6 space-y-3">
        {MILESTONES.map((m) => {
          const mDone = m.tasks.every((t) => state.done[t.id]);
          const mCount = m.tasks.filter((t) => state.done[t.id]).length;
          const isOpen = state.expanded === m.key;
          return (
            <div
              key={m.key}
              className="rounded-xl border border-border bg-background overflow-hidden"
              data-testid={`milestone-${m.key}`}
            >
              <button
                onClick={() => toggleExpand(m.key)}
                className="w-full flex items-center justify-between gap-4 px-4 md:px-5 py-4 hover:bg-secondary/50 transition-colors text-left"
              >
                <div className="flex items-center gap-4">
                  <div
                    className={`h-9 w-9 rounded-full grid place-items-center border ${
                      mDone
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-secondary text-muted-foreground border-border"
                    }`}
                  >
                    {mDone ? (
                      <Check className="h-4 w-4" strokeWidth={2} />
                    ) : (
                      <span className="text-[11px] font-mono">{mCount}/{m.tasks.length}</span>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="rounded-full text-[10px] uppercase tracking-wider">
                        {m.day}
                      </Badge>
                      <span className="font-heading text-base md:text-lg">{m.title}</span>
                    </div>
                    <p className="text-xs md:text-sm text-muted-foreground mt-1">{m.lead}</p>
                  </div>
                </div>
                <ChevronDown
                  className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform duration-200 ${
                    isOpen ? "rotate-180" : ""
                  }`}
                  strokeWidth={1.5}
                />
              </button>
              {isOpen && (
                <ul className="px-4 md:px-5 pb-4 space-y-2 border-t border-border pt-3">
                  {m.tasks.map((t) => {
                    const done = !!state.done[t.id];
                    return (
                      <li
                        key={t.id}
                        className="flex items-start justify-between gap-3 rounded-lg px-2 py-2 hover:bg-secondary/40 transition-colors"
                      >
                        <button
                          onClick={() => toggleTask(t.id)}
                          className="flex items-start gap-3 text-left flex-1"
                          data-testid={`onboarding-task-${t.id}`}
                        >
                          <span
                            className={`mt-0.5 h-5 w-5 shrink-0 rounded-md border grid place-items-center transition-colors ${
                              done
                                ? "bg-primary border-primary text-primary-foreground"
                                : "border-border bg-background"
                            }`}
                          >
                            {done && <Check className="h-3 w-3" strokeWidth={2.5} />}
                          </span>
                          <span
                            className={`text-sm leading-snug ${
                              done ? "text-muted-foreground line-through" : "text-foreground"
                            }`}
                          >
                            {t.label}
                          </span>
                        </button>
                        <Link to={t.to}>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground"
                            data-testid={`onboarding-goto-${t.id}`}
                          >
                            Öffnen <ArrowUpRight className="ml-1 h-3.5 w-3.5" strokeWidth={1.5} />
                          </Button>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      {complete && (
        <div className="mt-6 rounded-xl border border-primary/25 bg-primary/5 p-4 flex items-center justify-between gap-4">
          <p className="text-sm text-foreground/90">
            Deine ersten sieben Tage sind abgeschlossen — starker Start.
          </p>
          <Button
            variant="outline"
            size="sm"
            className="rounded-full"
            onClick={dismiss}
            data-testid="onboarding-finish"
          >
            Ausblenden
          </Button>
        </div>
      )}
    </section>
  );
}
